<template>
  <div>
    <v-select
      :value="1"
      :label="label"
      append-icon="ui-icon-arrow-down"
      :items="[1]"
      :data-selected-speciality-id="selectedItem.workplace?.spec.id"
      :menu-props="{
        disabled: true,
      }"
      outlined
      hide-details
      @click="openDialog"
    >
      <template #selection>
        <div class="text-break">
          <div v-if="selectedItem.isWorkplace">
            {{ transDynamic(selectedItem.workplace?.spec, `name,${language},${needTranslate}`) }} -
            {{ t('приём', options) }}
          </div>
          <div v-else>
            {{ transDynamic(selectedItem.service, `name,${language},${needTranslate}`) }}
          </div>
          <div class="text-body-2 text-secondary--text">
            <span v-if="selectedItem.isWorkplace">{{ formattedSelectPrice }}</span>
            <span v-else>{{ selectedItem.price }}</span>
            <template v-if="selectedItem.agesText && selectedItem.isWorkplace">
              <span
                v-if="selectedItem.price || formattedSelectPrice"
                class="bg-gray-80--text mx-1"
              >
                •
              </span>
              {{ selectedItem.agesText }}
            </template>
          </div>
        </div>
      </template>
    </v-select>
    <WorkplaceOrServiceDialog
      v-model="isDialogVisible"
      :lpu-id="lpuId"
      @item-selected="handleSelectItem"
      @input="handleInputEvent"
    />
  </div>
</template>

<script setup lang="ts">
import I18NextVue from 'i18next'
import {
  computed,
  nextTick,
  ref,
} from 'vue'
import { storeToRefs } from 'pinia'
import { getFormattedPrice } from 'www/utils'
import scrollLock from 'src/utils/scrollLock'
import transDynamic from 'djanjucks/filters/transDynamic'
import { useDoctorAppointmentCardStore } from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/stores'
import { useDoctorPageLpuListStore } from 'www/vue_components/common/DoctorPageLpuList/stores'
import WorkplaceOrServiceDialog from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/components/WorkplaceOrServiceDialog/WorkplaceOrServiceDialog.vue'
import { getPriceList } from 'www/vue_components/common/DoctorPageLpuList/functions'
import type { PlaceType } from 'www/vue_components/common/DoctorPriceBlock/types'
import type { ScheduleType } from 'www/api/apiScheduleTelemed.types'
import type { SelectWorkplaceOrServiceItem } from 'www/vue_components/common/DoctorPageLpuList/types'

const props = defineProps<{
  lpuId: number
  label?: string
}>()

const { t, language } = I18NextVue
const options = { ns: language }
const needTranslate = true

const isDialogVisible = ref(false)

const doctorAppointmentCardStore = useDoctorAppointmentCardStore(props.lpuId)()
const { selectedItem, scheduleType } = storeToRefs(doctorAppointmentCardStore)
const { requestCalendarSlots, resetSlots } = doctorAppointmentCardStore

const doctorPageLpuListStore = useDoctorPageLpuListStore()
const { filterType } = storeToRefs(doctorPageLpuListStore)

const formattedSelectPrice = computed(() => {
  if (
    selectedItem.value.workplace?.tab_is_free_with_treatment
    && filterType.value === 'clinic'
  ) {
    return t('Бесплатно', options)
  }

  const priceList = getPriceList(selectedItem.value.workplace)

  const { priceOnline, priceMedtochkaTelemed, priceClub } = priceList

  let price

  switch (filterType.value) {
    case 'clinic':
      price = priceList[typeof priceClub === 'number' ? 'priceClub' : 'price']
      break
    case 'home':
      price = priceList.priceHome
      break
    case 'telemed':
      price = priceOnline ?? priceMedtochkaTelemed
      break
  }

  if (typeof price !== 'number') {
    return ''
  }

  return getFormattedPrice(price, {
    prefix: filterType.value === 'home'
      || (filterType.value === 'clinic' && typeof priceClub === 'number')
      ? t('от', options)
      : '',
  })
})

function openDialog() {
  isDialogVisible.value = true

  scrollLock.disableScroll()
}

function handleInputEvent() {
  scrollLock.enableScroll()
}

async function afterNewItemSelected(prevFilterType: PlaceType, prevScheduleType: ScheduleType) {
  if (
    prevFilterType === 'telemed'
      && filterType.value === 'telemed'
      && prevScheduleType !== scheduleType.value
  ) {
    await requestCalendarSlots()
  }
}

async function handleSelectItem(item: SelectWorkplaceOrServiceItem) {
  const prevSelectedItem = selectedItem.value
  const prevFilterType = filterType.value
  const prevScheduleType = scheduleType.value

  selectedItem.value = item

  /**
   * nextTick нужен, чтобы обновились вычисляемые значения, от которых зависит запрос за слотами
   */
  await nextTick()
  await afterNewItemSelected(prevFilterType, prevScheduleType)

  if (filterType.value === 'telemed' || (item.isWorkplace && prevSelectedItem.isWorkplace)) {
    return
  }

  resetSlots()
  await requestCalendarSlots()
}
</script>

<style scoped lang="scss">
// Потому что есть глобальные стили с селектором `#app input`, которые ломают отображение
:deep(input) {
  height: 0 !important;
}
</style>
